<template>
  <div class="i-coaching-membership">
    <div class="row">
      <div class="column plan">
        <div>Your Plan:</div>
        <div class="main">{{ planTitle }}</div>
      </div>
      <div class="column">
        <div>Included in plan:</div>
        <div class="perks main">
          <div
            v-for="perk in dataToDisplay"
            :key="perk"
            class="perk"
          >
            <img
              :src="iconGreenCheckmark"
              class="icon"
            />
            {{ perk }}
          </div>
        </div>
      </div>
    </div>
    <div class="manage">
      <div class="info">
        <span
          v-if="isCoachingSubscriber"
          v-html="coachingRenewalDisplayData"
        >
        </span>
        <span
          v-if="isReportSubscriber"
          v-html="reportRenewalDisplayData"
        >
        </span>
      </div>

      <div class="option">
        <iBaseButton
          v-for="(cta, i) in dynamicCtaData.filter((e) => e.isVisible)"
          :key="i"
          v-bind="OTHER_BUTTON_STYLE"
          :text="cta.text"
          @clicked="cta.action()"
        />
      </div>
    </div>
    <iBaseModal
      class="embed"
      :visible="data.showConfirmModal"
      :fully-customisable="true"
      max-width="500px"
      @click-on-close="data.showConfirmModal = false"
    >
      <div class="confirm-skip-modal">
        <div class="warn">
          <strong>Important</strong>: This action will skip your next subscription renewal and will then renew on
          <strong>{{ formatedDate(skipRenewalTo) }}</strong
          >.<br /><br />
          You <strong>will not</strong> receive any new credits to book sessions with you coach until your subscription
          renews on this date.
        </div>
        <div class="actions">
          <iBaseButton
            text="Cancel"
            v-bind="OTHER_BUTTON_STYLE"
            @clicked="data.showConfirmModal = false"
          />
          <iBaseButton
            text="Confirm Renewal Skip"
            v-bind="OTHER_BUTTON_STYLE"
            :text-color="white"
            :background-color="bittersweet500"
            @clicked="extendSubscriptionPeriod"
          />
        </div>
      </div>
    </iBaseModal>
    <iBaseModal
      v-if="!!data.showCancelModal"
      class="embed"
      :visible="!!data.showCancelModal"
      :fully-customisable="true"
      max-width="500px"
      @click-on-close="closeCancelModal"
    >
      <div class="sub-cancel-quiz-modal">
        <div class="intro">
          <div>Before you go</div>
          <div class="sub">A few quick questions to help us improve.</div>
        </div>
        <div class="quiz">
          <div
            v-for="field in quizData.filter((e) => e.visible)"
            :key="field.key"
            class="question"
          >
            <div
              v-if="field.options"
              class="label"
            >
              {{ field.question }}
            </div>
            <select
              v-if="field.options"
              v-model="data.quiz[field.key as keyof typeof data.quiz]"
              class="select"
              :name="field.key"
              :placeholder="field.question"
            >
              <button>
                <selectedcontent></selectedcontent>
              </button>
              <option
                v-for="option in Object.keys(field.options)"
                :key="option"
                :value="option"
              >
                {{ field.options[option as keyof typeof field.options] }}
              </option>
            </select>

            <input
              v-else
              v-model="data.quiz[field.key as keyof typeof data.quiz]"
              type="text"
              :placeholder="field.question"
              spellcheck="false"
              class="select text"
              maxlength="100"
              autofocus
            />
          </div>
        </div>
        <div class="actions">
          <iBaseButton
            text="Keep my subscription"
            v-bind="OTHER_BUTTON_STYLE"
            @clicked="closeCancelModal"
          />
          <iBaseButton
            text="Continue to cancellation"
            v-bind="OTHER_BUTTON_STYLE"
            :text-color="white"
            :background-color="bittersweet500"
            :is-disabled="!quizCompleted"
            @clicked="quizCompleted ? submitSubscriptionCancellationQuizAnswers(data.showCancelModal) : null"
          />
        </div>
      </div>
    </iBaseModal>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, reactive } from "vue"
import { DateTime } from "luxon"
import { useUserStore } from "@/stores/user"
import { bittersweet500, black600, white } from "@/styles/colors"

import api from "@/services/apiClient"

import iBaseButton from "@/components/base/BaseButton.vue"
import iBaseModal from "@/components/base/BaseModal.vue"

// ASSETS
import iconGreenCheckmark from "@/assets/icons/icon-green-checkmark.svg"

// TYPES
import CoachingStatus from "@/shared/enums/CoachingStatus"
import ReportStatus from "@/shared/enums/ReportStatus"
import StripeBillingPortalType from "@/shared/enums/StripeBillingPortal"
import StripePrice from "@/shared/enums/StripePrice"
import Subscription from "@/shared/types/Subscription"
import QuizPostSubscriptionCancellationIn, {
  stayForDiscountMap,
  mainReasonMap,
  MainReasonType,
  OneToFiveType,
  possibleRetentionMap,
  PossibleRetentionType,
  progressMap,
  ProgressType,
  satisfactionRatingMap,
  valueRatingMap,
  YesNoMaybeType
} from "@/shared/dtos/in/QuizPostSubscriptionCancellationIn"

// STORES
const userStore = useUserStore()

// COSTANTS
const OTHER_BUTTON_STYLE = {
  backgroundColor: "transparent",
  borderRadius: "8px",
  fontWeight: "500",
  textColor: black600,
  width: "fit-content",
  padding: "10px 16px",
  size: "small",
  version: "outline"
}
const DEFAULT_COACHING_SUB_DATA = [
  "Weekly live group coaching",
  "Custom-built action plan",
  "Mock interviews with your coach",
  "Resume and LinkedIn guidance"
]
const DYNAMIC_COACHING_SUB_DATA: { [key in CoachingStatus]?: string } = {
  [CoachingStatus.EXECUTIVE]: "4 hours of 1:1 coaching each month",
  [CoachingStatus.PROFESSIONAL]: "2 hours of 1:1 coaching each month",
  [CoachingStatus.WEEKLY]: "2 hours of 1:1 coaching each month",
  [CoachingStatus.BIWEEKLY]: "1 hour of 1:1 coaching each month",
  [CoachingStatus.MONTHLY]: "30 mins of 1:1 coaching each month"
}
const DEFAULT_REPORT_SUB_DATA = [
  "Weekly live career coaching",
  "Top 10 career paths",
  "Detailed personality types and insights",
  "Tailored career strategy",
  "Resume and LinkedIn optimization"
]
const QUIZ_DATA_VALUES = {
  mainReason: null as MainReasonType | null,
  mainReasonOther: null as string | null,
  satisfactionRating: null as OneToFiveType | null,
  progress: null as ProgressType | null,
  valueRating: null as OneToFiveType | null,
  possibleRetention: null as PossibleRetentionType | null,
  possibleRetentionOther: null as string | null,
  stayForDiscount: null as YesNoMaybeType | null
}
const CHANGING_PLANS_PRICES = [
  StripePrice.COACHING_MONTHLY,
  StripePrice.COACHING_MONTHLY_HIGH,
  StripePrice.COACHING_CORE,
  StripePrice.COACHING_ACCELERATED,
  StripePrice.COACHING_WEEKLY,
  StripePrice.COACHING_BIWEEKLY,
  StripePrice.COACHING_PROFESSIONAL,
  StripePrice.COACHING_EXECUTIVE_997,
  StripePrice.COACHING_EXECUTIVE
]

// DATA
const data = reactive({
  showConfirmModal: false,
  showCancelModal: null as "coaching" | "report" | null,
  hasSkippedRenewal: false,
  coachingSubscription: null as Subscription | null,
  reportSubscription: null as Subscription | null,
  quiz: { ...QUIZ_DATA_VALUES }
})

// COMPUTED
const quizData = computed(() => {
  return [
    {
      key: "mainReason",
      visible: true,
      question: "What’s the main reason you’re canceling?",
      options: mainReasonMap(
        (data.showCancelModal === "coaching" ? data.coachingSubscription : data.reportSubscription)?.category
      )
    },
    {
      key: "mainReasonOther",
      visible: data.quiz.mainReason === "other",
      question: "Other reason:"
    },
    {
      key: "satisfactionRating",
      visible: true,
      question: "How satisfied were you with your overall experience?",
      options: satisfactionRatingMap
    },
    {
      key: "progress",
      visible: true,
      question: "How much progress did you make toward your original goal?",
      options: progressMap
    },
    {
      key: "valueRating",
      visible: true,
      question: `Do you feel ${data.showCancelModal === "coaching" ? "coaching" : "the value"} was worth what you paid?`,
      options: valueRatingMap
    },
    {
      key: "possibleRetention",
      visible: true,
      question: "Which of the following would have made you stay?",
      options: possibleRetentionMap(
        (data.showCancelModal === "coaching" ? data.coachingSubscription : data.reportSubscription)?.category
      )
    },
    {
      key: "possibleRetentionOther",
      visible: data.quiz.possibleRetention === "other",
      question: "Other:"
    },
    {
      key: "stayForDiscount",
      visible: data.showCancelModal === "coaching",
      question: "Would 25% off your next month of coaching help you stay?",
      options: stayForDiscountMap
    }
  ]
})
const quizCompleted = computed(() => {
  const visibleKeys = quizData.value.filter((item) => item.visible).map((item) => item.key)
  return visibleKeys.every((e) => {
    const value = data.quiz[e as keyof typeof data.quiz]
    return typeof value == "string" ? !!value.trim() : !!value
  })
})
const dynamicCtaData = computed(() => {
  return [
    {
      isVisible: true,
      text: "View & Manage Billing",
      action: () => openBillingPortalLogIn(StripeBillingPortalType.VIEW_MANAGE_BILLING)
    },
    {
      isVisible:
        isCoachingSubscriber.value &&
        data.coachingSubscription?.priceUuid &&
        CHANGING_PLANS_PRICES.includes(data.coachingSubscription?.priceUuid),
      text: "Change Plan",
      action: () => openBillingPortalLogIn(StripeBillingPortalType.CHANGE_PLAN)
    },
    {
      isVisible: isCoachingSubscriber.value && !data.coachingSubscription?.cancelAtPeriodEnd,
      text: `Cancel ${isReportSubscriber.value ? "Coaching" : ""}  Subscription`,
      action: () => (data.showCancelModal = "coaching")
    },
    {
      isVisible: isReportSubscriber.value && !data.reportSubscription?.cancelAtPeriodEnd,
      text: `Cancel ${isCoachingSubscriber.value ? "Report" : ""} Subscription`,
      action: () => (data.showCancelModal = "report")
    },
    {
      isVisible: isCoachingSubscriber.value && !!data.coachingSubscription?.cancelAtPeriodEnd,
      text: `Don't Cancel ${isReportSubscriber.value ? "Coaching" : "Subscription"}`,
      action: () => openBillingPortalLogIn(StripeBillingPortalType.CANCEL_COACHING_SUBSCRIPTION)
    },
    {
      isVisible: isReportSubscriber.value && !!data.reportSubscription?.cancelAtPeriodEnd,
      text: `Don't Cancel ${isCoachingSubscriber.value ? "Report" : "Subscription"}`,
      action: () => openBillingPortalLogIn(StripeBillingPortalType.CANCEL_REPORT_SUBSCRIPTION)
    },
    {
      isVisible: isCoachingSubscriber.value && !data.hasSkippedRenewal && !data.coachingSubscription?.cancelAtPeriodEnd,
      text: "Skip Next Renewal",
      action: () => (data.showConfirmModal = true)
    },
    {
      isVisible: !isCoachingSubscriber.value,
      text: "Contact Support",
      action: () => openUrl("mailto:support@jobtest.org")
    }
  ]
})
const isCoachingSubscriber = computed(() => !!data.coachingSubscription)
const isReportSubscriber = computed(() => !!data.reportSubscription)
const skipRenewalTo = computed(() =>
  data.coachingSubscription
    ? (DateTime.fromJSDate(data.coachingSubscription.currentPeriodEnd).plus({ months: 1 }).toJSDate() as Date)
    : null
)
const coachingRenewalDisplayData = computed(() =>
  data.hasSkippedRenewal
    ? `Your next coaching subscription has been successfully skipped and will now renew on <strong>${formatedDate(skipRenewalTo.value)}</strong>.`
    : data.coachingSubscription?.cancelAtPeriodEnd && formatedDate(data.coachingSubscription?.cancelAt)
      ? `Your coaching subscription cancels on <strong>${formatedDate(data.coachingSubscription?.cancelAt)}</strong>.`
      : formatedDate(data.coachingSubscription?.currentPeriodEnd)
        ? `Your coaching subscription renews on <strong>${formatedDate(data.coachingSubscription?.currentPeriodEnd)}</strong>.`
        : ""
)
const reportRenewalDisplayData = computed(() =>
  data.reportSubscription?.cancelAtPeriodEnd && formatedDate(data.reportSubscription?.cancelAt)
    ? `Your report subscription cancels on <strong>${formatedDate(data.reportSubscription?.cancelAt)}</strong>.`
    : formatedDate(data.reportSubscription?.currentPeriodEnd)
      ? `Your report subscription renews on <strong>${formatedDate(data.reportSubscription?.currentPeriodEnd)}</strong>.`
      : ""
)
const reportSubPlanTitle = computed(() =>
  userStore.getReportStatus === ReportStatus.MONTHLY
    ? "Monthly Subscription ($29.99/month)"
    : userStore.getReportStatus === ReportStatus.QUARTERLY
      ? "Quarterly Subscription ($59.97/3 months)"
      : "Annual Subscription ($119.88/year)"
)
const dataToDisplay = computed(() =>
  isCoachingSubscriber.value
    ? [DYNAMIC_COACHING_SUB_DATA[userStore.getCoachingStatus], ...DEFAULT_COACHING_SUB_DATA]
    : DEFAULT_REPORT_SUB_DATA
)
const planTitle = computed(() =>
  isCoachingSubscriber.value ? data.coachingSubscription?.name : reportSubPlanTitle.value
)

// FUNCTIONS
const formatedDate = (date?: Date | null) => {
  return date ? DateTime.fromJSDate(date).toFormat("LLL d, yyyy 'at' h:mm a") : null
}

function openUrl(redirectUrl: string) {
  window.open(redirectUrl, "_blank")
}

const fetchActiveCoachingSubscritpion = async () => {
  try {
    const userSubscriptions = await api.stripe.getUserActiveSubscriptions()
    data.coachingSubscription = userSubscriptions.coaching
    data.reportSubscription = userSubscriptions.report
    if (data.coachingSubscription) {
      data.coachingSubscription.currentPeriodEnd = new Date(userSubscriptions.coaching?.currentPeriodEnd)
      if (userSubscriptions.coaching?.cancelAt)
        data.coachingSubscription.cancelAt = new Date(userSubscriptions.coaching?.cancelAt)
    }
    if (data.reportSubscription) {
      data.reportSubscription.currentPeriodEnd = new Date(userSubscriptions.report?.currentPeriodEnd)
      if (userSubscriptions.report?.cancelAt)
        data.reportSubscription.cancelAt = new Date(userSubscriptions.report?.cancelAt)
    }
  } catch (error) {
    console.error("Error fetching this user's active subscriptions", error)
  }
}

const extendSubscriptionPeriod = async () => {
  if (data.coachingSubscription && skipRenewalTo.value) {
    try {
      await api.stripe.patchSubscriptionExtendPeriod(data.coachingSubscription.id, {
        newPeriodEnd: skipRenewalTo.value
      })
      data.hasSkippedRenewal = true
    } catch (error) {
      console.error("Error extending user's period end (sub renewal) date", error)
    } finally {
      data.showConfirmModal = false
    }
  }
}

const submitSubscriptionCancellationQuizAnswers = async (type: "coaching" | "report") => {
  const subscription = type === "coaching" ? data.coachingSubscription : data.reportSubscription
  const billingPortalType =
    type === "coaching"
      ? StripeBillingPortalType.CANCEL_COACHING_SUBSCRIPTION
      : StripeBillingPortalType.CANCEL_REPORT_SUBSCRIPTION

  if (subscription && quizCompleted) {
    const payload: QuizPostSubscriptionCancellationIn = {
      subscriptionId: subscription.id,
      mainReason: data.quiz.mainReason || "other",
      satisfactionRating: data.quiz.satisfactionRating || 1,
      progress: data.quiz.progress || "none",
      valueRating: data.quiz.valueRating || 1,
      possibleRetention: [data.quiz.possibleRetention || "other"],
      ...(type === "coaching" && { stayForDiscount: data.quiz.stayForDiscount || "no" }),
      ...(data.quiz.mainReason === "other" && { mainReasonOther: data.quiz.mainReasonOther?.trim() }),
      ...(data.quiz.possibleRetention === "other" && {
        possibleRetentionOther: data.quiz.possibleRetentionOther?.trim()
      })
    }

    try {
      await api.quiz.postSubscriptionCancellation(payload)
      await openBillingPortalLogIn(billingPortalType)
    } catch (error) {
      console.error("Error submitting user's subscription cancellation quiz answers", error)
    } finally {
      closeCancelModal()
    }
  }
}

const closeCancelModal = () => {
  data.showCancelModal = null
  data.quiz = { ...QUIZ_DATA_VALUES }
}

const openBillingPortalLogIn = async (type: StripeBillingPortalType) => {
  try {
    const billingPortal = await api.stripe.postBillingPortalLogin({ type })

    // Open the billing portal in the same tab (needed for safari)
    return (window.location.href = billingPortal.loginUrl)
  } catch (error) {
    console.error(`Error fetching billing portal login for this action :${type}`, error)
  }
}

onMounted(async () => {
  await fetchActiveCoachingSubscritpion()
})
</script>

<style scoped lang="scss">
.i-coaching-membership {
  @include flex(column, start, start);

  .row {
    @include flex(column, start, start, $gap: 15px);

    padding-bottom: 20px;

    @media screen and (min-width: $breakpoint-md) {
      @include flex(row, start, start);

      padding-bottom: 25px;
    }

    .column {
      @include flex(column, start, start, $gap: 5px);
      @include text-sm;

      font-weight: 600;
    }

    .plan {
      @media screen and (min-width: $breakpoint-md) {
        width: 250px;
        flex: 0 0 250px;
      }

      .main {
        @include text-md;

        font-weight: 700;
      }
    }

    .perks {
      @include text-sm;

      font-weight: 500;

      @media screen and (min-width: $breakpoint-xs) {
        @include text-md;
      }

      .icon {
        margin-right: 2px;
        width: 24px;
      }
    }
  }

  .manage {
    @include flex(column, start, start, $gap: 18px);
    @include text-md;

    font-weight: 500;

    .info {
      @include flex(column, start, start);
    }

    .option {
      @include flex(row, start, center, wrap, $gap: 10px);
    }
  }

  .embed {
    padding: 30px 0;

    @media screen and (min-width: $breakpoint-lg) {
      padding: unset;
    }

    .confirm-skip-modal {
      @include flex(column, start, stretch, $gap: 20px);

      padding: 30px 20px 20px;

      .warn {
        color: $bittersweet600;
        text-align: center;
      }

      .actions {
        @include flex(row, space-between, center, $gap: 10px);
      }
    }

    .sub-cancel-quiz-modal {
      @include flex(column, start, stretch, $gap: 20px);

      padding: 30px 20px 20px;

      .intro {
        @include text-xl;

        font-weight: 600;
        text-align: center;

        .sub {
          @include text-md;

          font-weight: 500;
        }
      }

      .quiz {
        @include flex(column, start, stretch, $gap: 10px);

        .question {
          @include flex(column, start, stretch, $gap: 4px);

          .label {
            @include text-sm;
          }

          .select {
            @include flex(row, space-between, center);
            @include border-radius-sm;
            @include text-sm;

            border: 1px solid $black200;
            color: $comet900;
            min-height: 35px;
            outline: none;
            width: 100%;
            appearance: base-select;
            cursor: pointer;
            padding: 0 15px;

            &.text {
              cursor: text;
            }

            &:hover,
            &:focus {
              background: $white;
            }

            option {
              @include text-xs;

              align-items: center;
              display: flex;
              justify-content: flex-start;
              gap: 10px;
              border: 1px solid $zumthor100;
              color: $comet600;
              min-height: 40px;
              padding: 5px 10px;
              text-wrap: auto;

              &:focus {
                background: $azure-radiance100;
              }

              &:first-of-type {
                border-radius: 8px 8px 0 0;
              }

              &:last-of-type {
                border-radius: 0 0 8px 8px;
              }

              &:not(option:last-of-type) {
                border-bottom: none;
              }

              @media screen and (min-width: $breakpoint-xs) {
                @include text-sm;
              }
            }
          }

          // styling for select and option below
          /* stylelint-disable */
          ::picker(select) {
            appearance: base-select;
            border: none;
          }
          /* stylelint-enable */
        }
      }

      .actions {
        @include flex(row, space-between, center, $gap: 10px);
      }
    }
  }
}
</style>
